import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/** Top-level crash guard. Catches render errors so a single broken window can't
 *  white-screen the whole desktop. Wire `onError` to Sentry in production. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error", error, info);
    const w = window as unknown as { Sentry?: { captureException: (e: unknown) => void } };
    w.Sentry?.captureException(error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen w-screen flex-col items-center justify-center gap-4 bg-[hsl(var(--bg-0))] px-6 text-center text-app">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-app-1">
          The interface hit an unexpected error. Your data is safe on the server.
        </p>
        <button
          className="rounded-xl bg-[hsl(var(--accent))] px-4 py-2 text-sm font-semibold text-[hsl(var(--bg-0))]"
          onClick={() => window.location.reload()}
        >
          Reload Hive
        </button>
      </div>
    );
  }
}
